import React from "react";
import { Button, IconButton, SystemMessage, Text } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import checkIcon from "../../assets/icons/check.svg";
import neutralInfoIcon from "../../assets/icons/neutral_info.svg";
import stoppedIcon from "../../assets/icons/stopped.svg";
import warningIcon from "../../assets/icons/warning.svg";
import {
  AdSystemT,
  FeatureFlagT,
  useAdSystemCampaignStateQuery,
  useCreditsQuery,
} from "../../graphql/generated/graphql";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { formatCurrencyUnit, formatMoney } from "../../i18n/formatNumbers";
import { getAdSystemCampaignState } from "../../utils/getAdSystemCampaignState";
import { ModalWrapper } from "../ModalWrapper";
import { SmallLoader } from "../SmallLoader";
import { DashboardCreditsModal } from "./DashboardCreditsModal";
import { getRemainingCreditDays } from "./getRemainingCreditDays";

const getCreditsLabelColor = (showCreditPaused: boolean, creditsBalance: number, remainingCreditDays: number) => {
  if (creditsBalance <= 0) {
    return "var(--app-critical)";
  } else if (showCreditPaused) {
    return "var(--app-text-muted)";
  } else if (remainingCreditDays < 5) {
    return "var(--app-warning)";
  }
  return "var(--app-primary)";
};

interface CreditsDashboardBoxProps {
  hideStatusMessage?: boolean;
}

const CreditsDashboardBox = ({ hideStatusMessage }: CreditsDashboardBoxProps) => {
  const [showAddCreditsModal, setShowAddCreditsModal] = React.useState(false);
  const [showCreditsInfoModal, setShowCreditsInfoModal] = React.useState(false);
  const { enabled: hasMetaCampaignsEnabled } = useFeatureFlag(FeatureFlagT.MetaCampaignsT);

  const { data: creditsData, loading: creditsLoading } = useCreditsQuery();

  const dateTo = new Date().toISOString().split("T")[0];

  // summaryStats and campaignOptimizations are scoped by $adSystem, so one response cannot describe both ad systems.
  const { data: googleAdsCampaignStateData, loading: googleAdsCampaignStateLoading } = useAdSystemCampaignStateQuery({
    variables: {
      adSystem: AdSystemT.GoogleAdsT,
      dateTo,
    },
  });
  const { data: metaAdsCampaignStateData, loading: metaAdsCampaignStateLoading } = useAdSystemCampaignStateQuery({
    variables: {
      adSystem: AdSystemT.MetaAdsT,
      dateTo,
    },
  });
  const campaignStatesLoading = googleAdsCampaignStateLoading || metaAdsCampaignStateLoading;

  const googleAdsCampaignState = getAdSystemCampaignState(googleAdsCampaignStateData, AdSystemT.GoogleAdsT);
  const metaAdsCampaignState = getAdSystemCampaignState(metaAdsCampaignStateData, AdSystemT.MetaAdsT);

  const { t } = useTranslation("translation", { keyPrefix: "Components.Credits" });
  const creditsBalance = creditsData?.organization?.creditBalance?.amount || 0;
  const currency = creditsData?.organization?.creditBalance?.currency || "CZK";
  const remainingCreditDays = getRemainingCreditDays(creditsData?.organization);
  const showCreditPaused =
    !campaignStatesLoading &&
    googleAdsCampaignState !== "active" &&
    metaAdsCampaignState !== "active" &&
    googleAdsCampaignState !== "active_optimizing" &&
    metaAdsCampaignState !== "active_optimizing";
  const showNoProductsMessage = googleAdsCampaignState === "no_products" || metaAdsCampaignState === "no_products";

  const renderCreditsMessage = () => {
    if (creditsBalance <= 0) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img alt="stopped icon" src={stoppedIcon} />
          <div>
            <Text>{t("Credit is exhausted.")}</Text>
          </div>
          <div>
            <Text appearance="strong-md">•</Text>
          </div>
          <div>
            <Text>
              <span style={{ color: "var(--app-text-muted)" }}>
                {t("Campaigns are paused - you can restart them by topping up your credit.")}
              </span>
            </Text>
          </div>
        </div>
      );
    } else if (showCreditPaused) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "16px",
              height: "16px",
              background: "var(--app-primary)",
              borderRadius: "50%",
            }}
          >
            <img alt="info" src={neutralInfoIcon} />
          </div>
          <div>
            <Text appearance="strong-md">{t("Credit is ready to launch")}</Text>
          </div>
          {remainingCreditDays > 0 && (
            <>
              <div>
                <Text appearance="strong-md">•</Text>
              </div>
              <div>
                <Text>
                  <span style={{ color: "var(--app-text-muted)" }}>
                    {t("After campaign start credit will be enough {{ days }} days.", {
                      days: remainingCreditDays,
                    })}
                  </span>
                </Text>
              </div>
            </>
          )}
        </div>
      );
    } else if (remainingCreditDays < 5) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img alt="warning icon" src={warningIcon} />
          <div>
            <Text>{t("You will soon run out of credit")}</Text>
          </div>
          <div>
            <Text appearance="strong-md">•</Text>
          </div>
          <div>
            <Text>
              <span style={{ color: "var(--app-text-muted)" }}>
                {t("The remaining amount will last for approximately {{ days }} days. Add credits", {
                  days: remainingCreditDays,
                })}
              </span>
            </Text>
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <img alt="Check icon" src={checkIcon} />
          <div>
            <Text appearance="strong-md">{t("Credit is drawn continuously.")}</Text>
          </div>
          <div>
            <Text appearance="strong-md">•</Text>
          </div>
          <div>
            <Text>
              <span style={{ color: "var(--app-text-muted)" }}>
                {t("The credit will last for approximately {{ days }} days.", {
                  days: remainingCreditDays,
                })}
              </span>
            </Text>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      {creditsBalance <= 0 && creditsData && (
        <div style={{ marginBottom: "24px" }}>
          <SystemMessage
            description={t("Zero credit message text")}
            level="error"
            title={t("Zero credit message title")}
          />
        </div>
      )}
      {showNoProductsMessage && (
        <div style={{ marginBottom: "24px" }}>
          <SystemMessage
            level="error"
            title={t("No products message title")}
            description={
              <Text>
                <Trans components={{ a: <a /> }} i18nKey={"No products message text"} t={t} />
              </Text>
            }
          />
        </div>
      )}
      <div style={{ border: "1px solid var(--app-border)", borderRadius: "var(--app-radius-box)", padding: "16px" }}>
        {creditsLoading && <SmallLoader />}
        {creditsData && !creditsLoading && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Text appearance="strong-lg">{t("Credit for shoptet campaigns")}</Text>
                {hasMetaCampaignsEnabled && (
                  <IconButton icon="shp-info" size="sm" ghost onClick={() => setShowCreditsInfoModal(true)} />
                )}
              </div>
              <div style={{ margin: "2px 0 8px" }}>
                <div data-test-id="current-credit-balance">
                  <span
                    style={{
                      fontWeight: "bold",
                      color: getCreditsLabelColor(showCreditPaused, creditsBalance, remainingCreditDays),
                      fontSize: "26px",
                      lineHeight: "1",
                    }}
                  >
                    {`${formatMoney({ amount: creditsBalance })} ${formatCurrencyUnit(currency)}`}
                  </span>
                </div>
              </div>
              {!hideStatusMessage && !campaignStatesLoading && renderCreditsMessage()}
            </div>
            <div>
              <Button
                data-test-id="credit-onetime-increase-button"
                variant="primary"
                onClick={() => setShowAddCreditsModal(true)}
              >
                {t("Top up credit")}
              </Button>
            </div>
          </div>
        )}
      </div>
      <DashboardCreditsModal
        isOpen={showAddCreditsModal}
        onClose={() => {
          setShowAddCreditsModal(false);
        }}
      />
      <ModalWrapper
        isOpen={showCreditsInfoModal}
        title={t("Credits info modal title")}
        maxWidth
        onClose={() => setShowCreditsInfoModal(false)}
      >
        <div>
          <Text appearance="strong-md">{t("Credits info modal text 1")}</Text>
        </div>
        <div style={{ marginBottom: "20px" }}>
          <Text>{t("Credits info modal text 2")}</Text>
        </div>
        <Text>{t("Credits info modal list title")}</Text>
        <ul style={{ margin: "0", paddingLeft: "15px" }}>
          <li>
            <Text>{t("Credits info modal list item 1")}</Text>
          </li>
          <li>
            <Text>{t("Credits info modal list item 2")}</Text>
          </li>
          <li>
            <Text>{t("Credits info modal list item 3")}</Text>
          </li>
        </ul>
      </ModalWrapper>
    </>
  );
};

export default CreditsDashboardBox;
